import React, { useEffect, useState } from 'react';
import { AttachmentIcon, DownloadIcon, MailIcon, MessageIcon } from 'assets/icons';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeam, selectCurrentTeamCustomersPermissions, showNotifier } from 'containers/app/appSlice';
import { CustomButton, CustomInput, ErrorScreenWithRetry, Loader } from 'components';
import { InputType } from 'components/custom-input.tsx/CustomInput';
import { NotifierTypes } from 'constants/notifierConstants';
import { REGEX } from 'constants/common';
import { twMerge } from 'tailwind-merge';
import { removeUSCountryCode } from 'utils/commonUtils';
import { useLazyDownloadEstimateAsPDFQuery, useLazyGetEstimateByIdQuery, useShareEstimateMutation } from '../api';
import { getPhoneNumberDigits, isValidPhoneNumber } from 'utils/phoneNumberUtils';

interface ShareEstimateSectionProps {
  estimateId: string;
  resetEstimatesQuery: () => void;
}

export const ShareEstimateSection: React.FC<ShareEstimateSectionProps> = ({ estimateId, resetEstimatesQuery }) => {
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);

  const [shareEstimate, { isSuccess: isEstimateShareSuccess }] = useShareEstimateMutation();
  const [downloadEstimate] = useLazyDownloadEstimateAsPDFQuery();
  const [
    getEstimateById,
    { data: estimateDataFromApi, isFetching: isLoadingEstimate, isError: isErrorEstimate, isSuccess: isEstimateSuccess }
  ] = useLazyGetEstimateByIdQuery();

  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);
  const [communicationMedium, setCommunicationMedium] = useState(null);
  const [updatePhoneNumber, setUpdatePhoneNumber] = useState(false);
  const [updateEmail, setUpdateEmail] = useState(false);
  const [emailUpdateDisabled, setEmailUpdateDisabled] = useState(false);
  const [phoneUpdateDisabled, setPhoneUpdateDisabled] = useState(false);
  const [emailCheckClicked, setEmailCheckClicked] = useState(false);
  const [phoneCheckClicked, setPhoneCheckClicked] = useState(false);

  const hasEditCustomerPermissions =
    customerPermissions.editCustomersCreatedByTeam || customerPermissions.editExistingCustomers;

  const handleGetEstimateById = () => {
    if (estimateId) {
      getEstimateById({ teamId: currentTeam.id, estimateId });
    }
  };

  useEffect(() => {
    handleGetEstimateById();
  }, [estimateId, currentTeam.id]);

  useEffect(() => {
    if (estimateDataFromApi) {
      const estimateData = estimateDataFromApi.data;
      const newPhoneNumber = removeUSCountryCode(estimateData.customer?.phoneNumber);
      const newEmail = estimateData.customer?.email;
      setPhoneNumber(newPhoneNumber);
      setEmail(newEmail);
      setPhoneUpdateDisabled(estimateData.customer?.phoneNumber === newPhoneNumber);
      setEmailUpdateDisabled(estimateData.customer?.email === newEmail);
      if (!phoneCheckClicked) setUpdatePhoneNumber(!phoneUpdateDisabled);
      if (!emailCheckClicked) setUpdateEmail(!emailUpdateDisabled);
    }
  }, [estimateDataFromApi]);

  useEffect(() => {
    if (isEstimateShareSuccess) {
      showShareSuccessNotifier();
      if (resetEstimatesQuery) resetEstimatesQuery();
    }
  }, [isEstimateShareSuccess]);

  const shareEstimateHandler = (communicationType: string) => {
    setCommunicationMedium(communicationType);
    let phoneNumberPayload = '';
    let emailPayload = '';
    const shareViaSMS = communicationType === 'SMS';
    let shouldUpdateCustomer = hasEditCustomerPermissions;
    if (shareViaSMS) {
      phoneNumberPayload = getPhoneNumberDigits(phoneNumber);
      shouldUpdateCustomer = hasEditCustomerPermissions && updatePhoneNumber;
    } else {
      emailPayload = email;
      shouldUpdateCustomer = hasEditCustomerPermissions && updateEmail;
    }
    const payload = {
      phoneNumber: phoneNumberPayload,
      email: emailPayload,
      updateCustomer: shouldUpdateCustomer,
      teamId: currentTeam.id
    };

    shareEstimate({ ...payload, estimateId, type: communicationType === 'SMS' ? 'sms' : 'email' });
  };

  const showShareSuccessNotifier = () => {
    dispatch(
      showNotifier({
        message: {
          primaryMessage: `Estimate shared successfully on ${communicationMedium === 'SMS' ? 'phone' : 'email'}`,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type: NotifierTypes.INFO,
        showClose: false,
        fontStyle: 'text-primary font-normal'
      })
    );
    // Add analytics logging here if needed
  };

  const downloadFile = async () => {
    try {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Downloading started in the background',
            secondaryMessage: '',
            isMessageHtml: true
          },
          type: NotifierTypes.INFO,
          showClose: false,
          fontStyle: 'text-primary font-normal'
        })
      );
      await downloadEstimate({ teamId: currentTeam.id, estimateId });
    } catch (error) {
      // console.error('Failed to export estimate as PDF:', error);
    }
  };

  const copyLinkHandler = () => {
    setIsLinkCopied(true);
    navigator.clipboard.writeText(estimateDataFromApi.data.publicUrl);
    setTimeout(() => setIsLinkCopied(false), 1000);
    // Add analytics logging here if needed
  };

  const isPhoneUpdateDisabled = () => {
    if (hasEditCustomerPermissions) {
      return phoneUpdateDisabled;
    }
    return !hasEditCustomerPermissions;
  };

  const isEmailUpdateDisabled = () => {
    if (hasEditCustomerPermissions) {
      return emailUpdateDisabled;
    }
    return !hasEditCustomerPermissions;
  };

  const handleShareSMS = () => {
    const phoneNumberDigits = getPhoneNumberDigits(phoneNumber);
    if (isValidPhoneNumber(phoneNumberDigits)) {
      shareEstimateHandler('SMS');
    } else setIsPhoneNumberValid(false);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="customNormalScroll flex w-full grow flex-col overflow-y-auto rounded py-2 px-4 pb-0">
        {isLoadingEstimate && (
          <div className="flex h-96 items-center justify-center">
            <Loader />
          </div>
        )}
        {isEstimateSuccess && !isLoadingEstimate && (
          <>
            <div className="text-px13 font-normal text-heading">
              Choose a method to share the estimate with the customer
            </div>
            <div className="mt-4 rounded-md border border-borderGray p-4">
              <div className="mb-4 flex items-center">
                <MessageIcon />
                <div className="ml-2 text-sbase font-semibold text-primaryText">Text Message</div>
              </div>
              <div>
                <div
                  className={`flex w-full rounded-lg border ${
                    isPhoneNumberValid ? 'border-borderGray' : 'border-error'
                  } bg-primary`}>
                  <div className="mt-0.5 mr-1 flex items-center justify-center pl-2.5 text-17px text-accentText">
                    +1
                  </div>
                  <CustomInput
                    placeholder="000-000-0000"
                    id="share-estimate-phone-number"
                    inputStyle="w-full text-17px"
                    type={InputType.TEXT}
                    className="h-10 rounded-lg bg-primary p-0 text-primaryText 
                placeholder:text-sbase placeholder:text-accentText focus:outline-none"
                    value={phoneNumber}
                    onChange={value => {
                      if (REGEX.phoneNumberInput.test(value)) {
                        setIsPhoneNumberValid(true);
                        setPhoneNumber(value);
                        setPhoneUpdateDisabled(value === estimateDataFromApi.data.customer?.phoneNumber);
                        setUpdatePhoneNumber(value !== estimateDataFromApi.data.customer?.phoneNumber);
                      }
                    }}
                  />
                </div>
                {!isPhoneNumberValid && <div className="mt-1 text-red-400">Please enter a valid phone number</div>}
                <CustomButton
                  id="share-estimate-sidepanel-send-sms-button"
                  className="mt-4 h-[38px] w-full bg-primaryBtn px-4 py-2 
              font-lato normal-case text-primaryBtnText disabled:opacity-70"
                  onClick={handleShareSMS}
                  StartIcon={MessageIcon}
                  startIconStyle="path-stroke-current">
                  <span className="text-sbase font-semibold">Send SMS</span>
                </CustomButton>
                <div
                  className={`mb-4 mt-2 flex items-center ${
                    isPhoneUpdateDisabled() && 'pointer-events-none opacity-50'
                  }`}>
                  <input
                    id="share-estimate-sidepanel-update-cust-info-phno-checkbox"
                    type="checkbox"
                    onChange={() => {
                      setPhoneCheckClicked(true);
                      setUpdatePhoneNumber(!updatePhoneNumber);
                    }}
                    checked={hasEditCustomerPermissions && updatePhoneNumber}
                    className="h-4 w-4 cursor-pointer rounded pl-1 accent-primaryBtn"
                  />
                  <label
                    htmlFor="share-estimate-sidepanel-update-cust-info-phno-checkbox"
                    className="ml-1 text-sbase font-medium text-primaryText">
                    Update customer information
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-md border border-borderGray p-4">
              <div className="mb-4 flex items-center">
                <MailIcon />
                <div className="ml-2 text-sbase font-semibold text-primaryText">Email</div>
              </div>
              <div>
                <CustomInput
                  id="share-estimate-email"
                  inputStyle="w-full text-17px"
                  type={InputType.TEXT}
                  onChange={value => {
                    setIsEmailValid(true);
                    setEmail(value);
                    setEmailUpdateDisabled(value === estimateDataFromApi.data.customer?.email);
                    setUpdateEmail(value !== estimateDataFromApi.data.customer?.email);
                  }}
                  value={email}
                  className={`block h-10 rounded-lg border ${
                    isEmailValid ? 'border-borderGray' : 'border-error'
                  } bg-primary p-2.5 font-semibold text-primaryText focus:outline-none`}
                  placeholder="Enter email id"
                />
                {!isEmailValid && <div className="mt-1 text-red-400">Please enter a valid email</div>}
                <CustomButton
                  id="share-estimate-sidepanel-email-button"
                  className="mt-4 h-[38px] w-full bg-primaryBtn 
              px-4 py-2 font-lato normal-case text-primaryBtnText disabled:opacity-70"
                  onClick={() => {
                    if (REGEX.email.test(email)) {
                      shareEstimateHandler('EMAIL');
                    } else setIsEmailValid(false);
                  }}
                  StartIcon={MailIcon}
                  startIconStyle="path-stroke-current">
                  <div className="font-semibold">Send Email</div>
                </CustomButton>
                <div
                  className={`mb-4 mt-2 flex items-center ${
                    isEmailUpdateDisabled() && 'pointer-events-none opacity-50'
                  }`}>
                  <input
                    id="share-estimate-sidepanel-update-cust-info-email-checkbox"
                    type="checkbox"
                    onChange={() => {
                      setEmailCheckClicked(true);
                      setUpdateEmail(!updateEmail);
                    }}
                    checked={hasEditCustomerPermissions && updateEmail}
                    className="h-4 w-4 cursor-pointer rounded pl-1 accent-primaryBtn"
                  />
                  <label
                    htmlFor="share-estimate-sidepanel-update-cust-info-email-checkbox"
                    className="ml-1 text-sbase font-medium text-primaryText">
                    Update customer information
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-md border border-borderGray p-4">
              <div className="mb-4 flex items-center">
                <AttachmentIcon />
                <div className="ml-2 text-sbase font-semibold text-primaryText">Copy Link</div>
              </div>
              <div
                className="flex rounded-lg border border-borderGray 
            text-gray-600 focus-within:text-gray-400 focus:border-primaryBtn">
                <div
                  className="block w-full truncate rounded-l-lg 
              bg-primary p-2.5 text-sbase text-primaryText focus:ring-blue-500">
                  {estimateDataFromApi.data.publicUrl}
                </div>
                <button
                  onClick={copyLinkHandler}
                  className={twMerge(`py-3 flex h-full flex-1 flex-row items-center justify-center rounded-r-lg border-l
              border-borderGray bg-secondaryBtn font-semibold
               px-4 text-sbase text-primaryBtn ${isLinkCopied && 'text-primaryText'}`)}
                  style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {isLinkCopied ? 'Link copied' : 'Copy'}
                </button>
              </div>
            </div>

            <div className="mt-4 flex justify-center">
              <div id="download-estimate-button" className="flex cursor-pointer items-center" onClick={downloadFile}>
                <DownloadIcon />
                <div className="ml-2 text-17px font-semibold text-secondary">Download Estimate</div>
              </div>
            </div>
          </>
        )}

        {isErrorEstimate && !isLoadingEstimate && (
          <div>
            <ErrorScreenWithRetry handleReload={handleGetEstimateById} />
          </div>
        )}
      </div>
    </div>
  );
};
